import { useParams, Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { SEOHead, generateBreadcrumbSchema, generateArticleSchema } from '@/components/base/SEO';
import { comics, categories } from '@/mocks/comics';
import { siteInfo } from '@/mocks/site';
import { useEffect } from 'react';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find(c => c.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4"></i>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">您要查看的漫画不存在或已被移除</p>
            <RouterLink to="/" className="inline-block px-6 py-2.5 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </RouterLink>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories.find(c => c.slug === comic.category);
  const relatedComics = comics.filter(c => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  const canonicalUrl = `${siteInfo.url}/article/${comic.id}`;
  const breadcrumbItems = [
    { name: '首页', url: siteInfo.url },
    { name: categoryInfo?.name || '漫画', url: `${siteInfo.url}/category/${comic.category}` },
    { name: comic.title, url: canonicalUrl },
  ];

  const ratingStars = Array.from({ length: 5 }, (_, i) => {
    const starVal = comic.rating / 2;
    if (i < Math.floor(starVal)) return 'ri-star-fill text-amber-400';
    if (i < starVal) return 'ri-star-half-fill text-amber-400';
    return 'ri-star-line text-foreground-400';
  });

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author}`}
        description={`${comic.title} - ${comic.author}作品，${comic.description} 韩漫网免费提供无删减高清韩国漫画在线阅读。`}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫,无删减漫画,${categoryInfo?.name || ''}`}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={comic.cover}
        articlePublishedTime={`${comic.updatedAt}T00:00:00+08:00`}
        articleModifiedTime={`${comic.updatedAt}T00:00:00+08:00`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([
          generateBreadcrumbSchema(breadcrumbItems),
          generateArticleSchema({
            title: comic.title,
            description: comic.description,
            image: comic.cover,
            url: canonicalUrl,
            publishedDate: `${comic.updatedAt}T00:00:00+08:00`,
            modifiedDate: `${comic.updatedAt}T00:00:00+08:00`,
            author: comic.author,
          }),
        ]) }}
      />

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[
            { label: categoryInfo?.name || '漫画', href: `/category/${comic.category}` },
            { label: comic.title },
          ]} />

          <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-8">
            {/* Article Hero */}
            <article className="flex flex-col md:flex-row gap-8 mb-12">
              <div className="flex-shrink-0">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - ${comic.author} - 韩国漫画封面`}
                  title={`${comic.title} - 无删减韩国漫画在线阅读`}
                  className="w-48 md:w-64 rounded-lg object-cover object-top"
                  width={256}
                  height={360}
                />
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>
                <p className="text-foreground-600 mt-2">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-0.5">
                    {ratingStars.map((cls, i) => (
                      <i key={i} className={cls}></i>
                    ))}
                  </div>
                  <span className="text-lg font-bold text-foreground-950">{comic.rating}</span>
                  <span className="text-foreground-500">· {(comic.views / 10000).toFixed(0)}万阅读</span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <span className={`text-sm px-3 py-1 rounded-full font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-100 text-primary-600' : 'bg-foreground-100 text-foreground-600'}`}>
                    {comic.status}
                  </span>
                  <span className="text-sm px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
                    <i className="ri-book-open-line mr-1"></i>{comic.chapters}话
                  </span>
                  <span className="text-sm px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
                    <i className="ri-folder-line mr-1"></i>{categoryInfo?.name || comic.category}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map(tag => (
                    <span key={tag} className="text-sm px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-wrap gap-3 mt-6">
                  <button className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line mr-1"></i>开始阅读
                  </button>
                  <button className="px-6 py-2.5 rounded-md border border-background-200/70 text-foreground-700 font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1"></i>加入收藏
                  </button>
                  <button className="px-6 py-2.5 rounded-md border border-background-200/70 text-foreground-700 font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-line mr-1"></i>分享
                  </button>
                </div>
              </div>
            </article>

            {/* Chapter List */}
            <section className="mb-12">
              <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                章节列表
              </h2>
              <div className="border border-background-200/70 rounded-lg overflow-hidden">
                <div className="bg-background-100 px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-700">共 {comic.chapters} 话</span>
                  <button className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                    倒序 <i className="ri-arrow-up-down-line"></i>
                  </button>
                </div>
                <div className="divide-y divide-background-200/70">
                  {comic.chapterList?.map((ch, idx) => (
                    <a
                      key={ch.num}
                      href="#"
                      className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors">
                          {ch.title}
                        </span>
                        {idx === 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-600 whitespace-nowrap">NEW</span>
                        )}
                      </div>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.date}</span>
                    </a>
                  ))}
                  {comic.chapters > (comic.chapterList?.length || 0) && (
                    <div className="px-4 py-3 text-center">
                      <button className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                        查看更多章节 <i className="ri-arrow-down-s-line"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="pb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                      相关推荐
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">同类型热门漫画</p>
                  </div>
                  <RouterLink to={`/category/${comic.category}`} className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer">
                    更多 <i className="ri-arrow-right-line"></i>
                  </RouterLink>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {relatedComics.map(rc => (
                    <ComicCard key={rc.id} comic={rc} size="small" />
                  ))}
                </div>
              </section>
            )}
          </main>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}